// src/lib/ideaText.ts
//
// Texte d'une idée envoyée, lu par le portail, l'admin et les e-mails à l'équipe (sans code
// serveur, pour que le portail puisse l'importer). Le parcours d'idée envoie au serveur un titre
// et un texte qui colle deux phrases : « Avant, … Depuis que j'utilise l'app Basalt Lab, … ». Il ne
// demande plus de première phrase (`hook`) : elle reste vide pour une nouvelle idée, une idée
// ancienne garde la sienne et l'équipe la voit toujours. Ici, ce texte se recoupe en ses deux
// phrases, et l'idée se présente en blocs étiquetés, dans l'ordre où la personne les a remplis.
// Une idée écrite avant le parcours (texte libre) reste en un seul bloc « Idée ».

export const BEFORE_LEAD = 'Avant,'
export const AFTER_LEAD = "Depuis que j'utilise l'app Basalt Lab,"

// « Basalt\sLab » : la règle de texte du portail ne lit que « l'app » suivi d'une espace.
const SPLIT = /^Avant,\s+([\s\S]+?)\s+Depuis que j['’]utilise l['’]app\sBasalt\sLab,\s+([\s\S]+)$/u

/** Première phrase sans les guillemets tapés par la personne : chaque page ajoute les siens. */
export function hookText(hook: string): string {
  return hook.replace(/^[\s«"“]+|[\s»"”]+$/gu, '')
}

/** Les deux phrases d'une idée envoyée ; `matched` faux quand le texte ne suit pas la forme. */
export function splitIdea(text: string): { before: string; after: string; matched: boolean } {
  const match = SPLIT.exec(text.trim())
  if (!match?.[1] || !match[2]) return { before: text, after: '', matched: false }
  return { before: match[1].trim(), after: match[2].trim(), matched: true }
}

export type IdeaPartKey = 'title' | 'before' | 'after' | 'idea' | 'hook'

/** Libellés vus par l'équipe : les mots du parcours d'idée (jamais « Accroche »). */
export const IDEA_LABELS: Record<IdeaPartKey, string> = {
  title: 'Titre de la vidéo',
  before: 'Avant…',
  after: "Depuis que j'utilise l'app Basalt Lab…",
  idea: 'Idée',
  hook: 'Première phrase (les 2 premières secondes)',
}

export type IdeaPart = { key: IdeaPartKey; label: string; text: string }

/**
 * Première phrase vue par l'équipe : les guillemets qui l'entourent tout entière s'enlèvent (la
 * fiche met les siens), jamais ceux d'une citation qui la termine (« Il m'a dit « jamais » »).
 * Une phrase faite seulement de guillemets reste telle quelle plutôt que de disparaître.
 */
function displayHook(hook: string): string {
  const text = hook.trim()
  const wrapped = /^[«"“]\s*([\s\S]*?)\s*[»"”]$/u.exec(text)
  return wrapped?.[1] ? wrapped[1] : text
}

/**
 * L'idée en blocs étiquetés : titre, « Avant… », « Depuis que j'utilise l'app Basalt Lab… »,
 * première phrase (entre guillemets) ; une idée ancienne garde son texte en un bloc « Idée ».
 * Un bloc vide n'apparaît pas.
 */
export function ideaParts(video: { title: string; idea: string; hook: string }): IdeaPart[] {
  const { before, after, matched } = splitIdea(video.idea)
  const hook = displayHook(video.hook)
  const story: [IdeaPartKey, string][] = matched
    ? [
        ['before', before],
        ['after', after],
      ]
    : [['idea', video.idea.trim()]]
  const parts: [IdeaPartKey, string][] = [['title', video.title.trim()], ...story, ['hook', hook === '' ? '' : `« ${hook} »`]]
  return parts.filter(([, text]) => text !== '').map(([key, text]) => ({ key, label: IDEA_LABELS[key], text }))
}
